// Utility functions for handling video URLs and converting them to embed URLs

export function getVideoEmbedUrl(url: string): string | null {
  try {
    // Ensure url is a string and not empty
    if (!url || typeof url !== 'string') {
      return null;
    }
    
    const urlObj = new URL(url);
    
    // YouTube URLs
    if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
      let videoId = '';
      
      if (urlObj.hostname.includes('youtu.be')) {
        // Handle youtu.be/VIDEO_ID format
        videoId = urlObj.pathname.slice(1);
      } else if (urlObj.hostname.includes('youtube.com')) {
        // Handle youtube.com/watch?v=VIDEO_ID format
        videoId = urlObj.searchParams.get('v') || '';
      }
      
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Vimeo URLs
    if (urlObj.hostname.includes('vimeo.com')) {
      const videoId = urlObj.pathname.split('/').pop();
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }
    
    // Loom URLs
    if (urlObj.hostname.includes('loom.com')) {
      const pathParts = urlObj.pathname.split('/');
      const shareIndex = pathParts.indexOf('share');
      if (shareIndex !== -1 && pathParts[shareIndex + 1]) {
        const videoId = pathParts[shareIndex + 1];
        return `https://www.loom.com/embed/${videoId}`;
      }
    }
    
    // Wistia URLs
    if (urlObj.hostname.includes('wistia.com') || urlObj.hostname.includes('wistia.net')) {
      let videoId = '';
      
      // Handle fast.wistia.net/embed/iframe/VIDEO_ID (already embed URL)
      if (urlObj.pathname.includes('/embed/iframe/')) {
        return url;
      }
      
      // Handle fast.wistia.com/embed/VIDEO_ID.js
      if (urlObj.pathname.includes('/embed/') && urlObj.pathname.endsWith('.js')) {
        videoId = urlObj.pathname.split('/embed/')[1]?.replace('.js', '');
      }
      // Handle *.wistia.com/medias/VIDEO_ID
      else if (urlObj.pathname.includes('/medias/')) {
        videoId = urlObj.pathname.split('/medias/')[1]?.split('/')[0];
      }
      // Handle other Wistia URL formats
      else {
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        if (pathParts.length > 0) {
          videoId = pathParts[pathParts.length - 1].replace('.js', '');
        }
      }
      
      if (videoId) {
        return `https://fast.wistia.net/embed/iframe/${videoId}`;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing video URL:', error);
    return null;
  }
}

export function getVideoType(url: string): 'youtube' | 'vimeo' | 'loom' | 'wistia' | 'unknown' {
  try {
    // Ensure url is a string and not empty
    if (!url || typeof url !== 'string') {
      return 'unknown';
    }
    
    const urlObj = new URL(url);
    
    if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
      return 'youtube';
    }
    
    if (urlObj.hostname.includes('vimeo.com')) {
      return 'vimeo';
    }
    
    if (urlObj.hostname.includes('loom.com')) {
      return 'loom';
    }
    
    if (urlObj.hostname.includes('wistia.com') || urlObj.hostname.includes('wistia.net')) {
      return 'wistia';
    }
    
    return 'unknown';
  } catch (error) {
    return 'unknown';
  }
}

export function isVideoUrl(url: string): boolean {
  // Ensure url is a string and not empty
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  const videoType = getVideoType(url);
  return videoType !== 'unknown';
}